import React from 'react'
import classnames from 'classnames/bind'
import {
  Button,
  CardContainer,
  Icon,
  Tab,
  Text,
  WireInstructionField,
  constants,
  hooks,
} from '@adc/ui-core'
import type { SlottableComponent, AutomationTarget } from '@adc/ui-core'
import {
  IL_DEPOSIT_WIRE_DETAILS,
  IL_DEPOSIT_CHECK_DETAILS,
  IL_DEPOSIT_CONTACT_EMAIL,
  IL_DEPOSIT_CONTACT_PHONE,
  WireDetails,
  CheckDetails,
} from './constants'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const { ColorNames } = constants

export const formatDepositAmount = (amount: number): string =>
  `$${amount.toLocaleString('en-US')}`

const MultiLineValue = ({ lines }: { lines: string[] }) => (
  <div>
    {lines.map(line => (
      <div key={line}>{line}</div>
    ))}
  </div>
)

export interface PaymentInstructionsContentProps
  extends SlottableComponent<'header'>,
    AutomationTarget {
  depositAmount: number
  bidderId: string
  wireDetails?: WireDetails
  checkDetails?: CheckDetails
  onDownload?: () => void
  onEmail?: () => void
  contactEmail?: string
  contactPhone?: string
  stickyFooter?: boolean
}

const PaymentInstructionsContent = ({
  slots,
  depositAmount,
  bidderId,
  wireDetails = IL_DEPOSIT_WIRE_DETAILS,
  checkDetails = IL_DEPOSIT_CHECK_DETAILS,
  onDownload,
  onEmail,
  contactEmail = IL_DEPOSIT_CONTACT_EMAIL,
  contactPhone = IL_DEPOSIT_CONTACT_PHONE,
  elmId = 'payment_instructions_content',
  stickyFooter = false,
}: PaymentInstructionsContentProps) => {
  const breakpoint = hooks.useBreakpoints()
  const isMobile = breakpoint === 'mobile'
  const isSticky = stickyFooter && isMobile
  const wirePane = (
    <CardContainer
      className={cx('fields-container')}
      elmId='payment_instructions_wire_fields'
      borderRadius='small'
      padding='small'
      borderColor={ColorNames.GrayLight}
      borderStyle='line'
    >
      <WireInstructionField
        label='Bank Name'
        value={wireDetails.bank_name}
        elmId='wire_instructions_bank_name'
        level={8}
      />
      <WireInstructionField
        label='Bank Address'
        value={wireDetails.bank_address}
        elmId='wire_instructions_bank_address'
        level={8}
      />
      <WireInstructionField
        label='ABA Routing No.'
        value={wireDetails.routing_number}
        elmId='wire_instructions_aba_routing_number'
        level={8}
      />
      <WireInstructionField
        label='Account No.'
        value={wireDetails.account_number}
        elmId='wire_instructions_account_number'
        level={8}
      />
      <WireInstructionField
        label='Beneficiary Account Name'
        value={wireDetails.beneficiary_account_name}
        elmId='wire_instructions_beneficiary_account_name'
        level={8}
      />
      <WireInstructionField
        label='Beneficiary Address'
        value={wireDetails.beneficiary_address}
        elmId='wire_instructions_beneficiary_address'
        level={8}
      />
      <WireInstructionField
        label='Reference (Your Bidder ID)'
        value={bidderId}
        elmId='wire_instructions_reference'
        level={8}
      />
    </CardContainer>
  )

  const checkPane = (
    <CardContainer
      className={cx('fields-container')}
      elmId='payment_instructions_check_fields'
      borderRadius='small'
      padding='small'
      borderColor={ColorNames.GrayLight}
      borderStyle='line'
    >
      <WireInstructionField
        label='Make payable to'
        value={checkDetails.make_payable_to}
        elmId='payment_instructions_make_payable_to'
        level={8}
      />
      <WireInstructionField
        label='Amount'
        value={formatDepositAmount(depositAmount)}
        elmId='payment_instructions_amount'
        level={8}
      />
      <WireInstructionField
        label='Memo (Your Bidder ID)'
        value={bidderId}
        elmId='payment_instructions_memo'
        level={8}
      />
      <WireInstructionField
        label='Drop off at'
        value={<MultiLineValue lines={checkDetails.drop_off_at} />}
        elmId='payment_instructions_drop_off_at'
        level={8}
      />
      <WireInstructionField
        label='Hours'
        value={<MultiLineValue lines={checkDetails.hours} />}
        elmId='payment_instructions_hours'
        level={8}
      />
    </CardContainer>
  )

  const panes = [
    {
      menuItem: {
        key: 'wire-transfer',
        content: 'Wire Transfer',
        'data-elm-id': 'payment_instructions_wire_transfer_tab',
      },
      render: () => <Tab.Pane attached={false}>{wirePane}</Tab.Pane>,
    },
    {
      menuItem: {
        key: 'hand-delivered-check',
        content: "Cashier's Check",
        'data-elm-id': 'payment_instructions_check_tab',
      },
      render: () => <Tab.Pane attached={false}>{checkPane}</Tab.Pane>,
    },
  ]

  return (
    <div className={cx('payment-instructions-content')} data-elm-id={elmId}>
      {slots?.header?.()}
      <Tab
        elmId='payment_instructions_tabs'
        role='secondary'
        panes={panes}
        collapsing={false}
      />
      <div className={cx({ 'cta-footer--sticky': isSticky })}>
        <div className={cx('cta-container')}>
          {onDownload && (
            <Button
              role='secondary'
              onClick={onDownload}
              elmId='payment_instructions_download'
            >
              <Icon
                iconStyle='regular'
                name='download'
                color='inherit'
                className={cx('cta-icon')}
              />
              Download Instructions
            </Button>
          )}
          {onEmail && (
            <Button
              role='secondary'
              onClick={onEmail}
              elmId='payment_instructions_email'
            >
              <Icon
                iconStyle='regular'
                name='paper-plane'
                color='inherit'
                className={cx('cta-icon')}
              />
              Email Instructions
            </Button>
          )}
        </div>
        <Text
          size={isMobile ? 'mini' : 'small'}
          className={cx('help-footer')}
          elmId='payment_instructions_help_footer'
        >
          Need help? Contact{' '}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a> or{' '}
          <a href={`tel:${contactPhone.replace(/[^+\d]/g, '')}`}>
            {contactPhone}
          </a>
        </Text>
      </div>
    </div>
  )
}

export default PaymentInstructionsContent
